import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SearchBox from "@/components/base/SearchBox";
import EntryCard from "@/components/feature/EntryCard";
import Reveal from "@/components/base/Reveal";
import useSeo from "@/hooks/useSeo";
import { entries } from "@/mocks/entries";
import { hotKeywords, initialGroups } from "@/mocks/keywords";
import { categories } from "@/mocks/categories";

const tabs = [{ slug: "all", name: "全部" }, ...categories.map((item) => ({ slug: item.slug, name: item.name }))];

export default function KeywordsPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const trimmed = query.trim();
  const [active, setActive] = useState("all");

  const matchedEntries = useMemo(() => {
    const keyword = trimmed.toLowerCase();
    if (!keyword) return [];
    return entries
      .filter((entry) => {
        if (active !== "all" && entry.category !== active) return false;
        return (
          entry.title.toLowerCase().includes(keyword) ||
          entry.persona.toLowerCase().includes(keyword) ||
          entry.summary.toLowerCase().includes(keyword) ||
          entry.tags.some((tag) => tag.toLowerCase().includes(keyword))
        );
      })
      .sort((a, b) => b.heat - a.heat);
  }, [trimmed, active]);

  const keywordList = useMemo(
    () =>
      hotKeywords
        .filter((item) => active === "all" || item.category === active)
        .filter((item) => !trimmed || item.word.includes(trimmed))
        .sort((a, b) => b.heat - a.heat),
    [active, trimmed],
  );

  const grouped = useMemo(
    () =>
      initialGroups
        .map((letter) => ({ letter, words: keywordList.filter((item) => item.initial === letter) }))
        .filter((group) => group.words.length > 0),
    [keywordList],
  );

  const origin = window.location.origin;

  useSeo({
    title: trimmed
      ? `${trimmed} - 黑料关键词搜索结果 - 15黑料网`
      : "热门黑料词索引 - 黑料标签与分类查找 - 15黑料网",
    description: trimmed
      ? `在15黑料网检索「${trimmed}」相关的黑料汇总与标签内容，按分类查看匹配词条，快速定位人物或事件对应的索引结果。`
      : "热门黑料词索引按热度排序，支持黑料标签、分类查找与拼音首字母分组浏览。持续更新热门黑料词，输入人物或事件名称即可找到对应汇总。",
    keywords: "黑料索引,热门黑料词,黑料标签,分类查找,关键词搜索",
    path: "/keywords",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: trimmed ? `${trimmed} 的检索结果` : "热门黑料词索引",
        url: `${origin}/keywords`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: "15黑料网", url: origin },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: origin },
          { "@type": "ListItem", position: 2, name: "热门索引", item: `${origin}/keywords` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "热门黑料词",
        itemListElement: keywordList.slice(0, 12).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.word,
          url: `${origin}/entry/${item.entryId}`,
        })),
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "热门索引" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              热门黑料词索引
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              用关键词搜索、标签匹配或分类筛选，在 3,208 个热门黑料词中定位目标内容。输入人物全名的命中率通常高于输入外号。
            </p>
            <div className="mt-6 max-w-3xl">
              <SearchBox key={trimmed} defaultValue={trimmed} variant="large" />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.slug}
                type="button"
                onClick={() => setActive(tab.slug)}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                  active === tab.slug ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>

          {trimmed && (
            <div className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                「{trimmed}」相关汇总
                <span className="ml-2 text-sm font-normal text-foreground-500">{matchedEntries.length} 条结果</span>
              </h2>
              {matchedEntries.length > 0 ? (
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {matchedEntries.map((entry) => (
                    <EntryCard key={entry.id} entry={entry} />
                  ))}
                </div>
              ) : (
                <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100/60 p-8 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-search-line text-xl" />
                  </span>
                  <p className="mt-3 text-sm text-foreground-700">
                    暂时没有匹配「{trimmed}」的汇总词条，可以试试更短的关键词，或从下面的热门词里找找看。
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-10">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                黑料词列表
                <span className="ml-2 text-sm font-normal text-foreground-500">{keywordList.length} 个词</span>
              </h2>
              <div className="flex flex-wrap gap-2 text-xs text-foreground-500">
                {grouped.map((group) => (
                  <a
                    key={group.letter}
                    href={`#letter-${group.letter}`}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-background-200 bg-background-50 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    {group.letter}
                  </a>
                ))}
              </div>
            </div>

            {grouped.length > 0 ? (
              <div className="mt-6 space-y-8">
                {grouped.map((group) => (
                  <div key={group.letter} id={`letter-${group.letter}`} className="scroll-mt-32">
                    <h3 className="flex items-center gap-3 font-heading text-base font-bold text-primary-600">
                      {group.letter}
                      <span className="h-px flex-1 bg-background-200" />
                    </h3>
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {group.words.map((word, index) => (
                        <Reveal key={word.word} delay={Math.min(index * 30, 200)}>
                          <Link
                            to={`/entry/${word.entryId}`}
                            className="group flex items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300"
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600">
                                {word.word}
                              </span>
                              <span className="mt-0.5 block text-xs text-foreground-500">
                                {
                                  categories.find((category) => category.slug === word.category)?.name
                                }{' '}
                                · 点击查看汇总
                              </span>
                            </span>
                            <span className="flex shrink-0 items-center gap-1 text-xs tabular-nums text-accent-700">
                              <i className="ri-fire-line" />
                              {word.heat}
                            </span>
                          </Link>
                        </Reveal>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-6 rounded-lg border border-dashed border-background-300 p-8 text-center text-sm text-foreground-600">
                当前分类下没有匹配的黑料词，换个分类或清空搜索词试试。
              </p>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}